import { useState, useEffect, useCallback, useMemo } from 'react'
import { Input, Popover, notification, Dropdown } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import BarcodeIcon from '../Svg/BarcodeIcon'
import { saveBarcode } from '../../Slices/Kiosk/TableOptionsSlice'
import { getBarcode } from '../../Services/Kiosk/BarcodeService'
import { populateTableWithArticles, fetchAllArticleDetails } from '../../Services/Kiosk/ArticleServices'
import {
  HandleClientBarcode,
  Handle4CharacterBarcode,
  Handle5CharacterBarcode,
  Handle6CharacterBarcode,
  Handle7CharacterBarcode,
  Handle8CharacterBarcode,
  Handle9CharacterBarcode,
  Handle10CharacterBarcode,
  Handle11CharacterBarcode,
  Handle12CharacterBarcode,
  Handle13CharacterBarcode,
  Handle14CharacterBarcode,
  Handle15CharacterBarcode,
  Handle1617CharacterBarcode,
  Handle18CharacterBarcode,
} from '../../Helpers/BarcodeHelpers'
import { ProfilesKeys } from '../../Constants/Configuration'
import { ArticleColumn } from '../../Constants/TableConfiguration'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { handleErrorCatchLog, renderTranslation } from '../../Helpers/GenericHelpers'
import { setShowBarcodeElement } from '../../Slices/ConfigurationSlice'
import { useAppDispatch, useAppSelector } from '../../hooks'

function Barcode() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const activeAutocomplete = useAppSelector(state => state.present.tableOptions.activeAutocomplete)
  const profileValue = useAppSelector(state => state.present.configuration.profileValue)
  const barcodeElementVisibile = useAppSelector(state => state.present.configuration.workspaceBarcodeElementVisible)
  const formConfigurationValue = useAppSelector(state => state.present.configuration.formConfigurationValue)
  const movementTabVisible = useAppSelector(state => state.present.configuration.isTabMovementVisible)
  const workspaceMovementVisible = useAppSelector(state => state.present.configuration.workspaceMovementVisible)

  const [barCodeVisible, setBarCodeVisible] = useState(false)
  const [barCode, setBarCode] = useState('')

  const hideSectionMenu = useMemo(() => {
    return {
      items: [
        {
          key: 'disable',
          label: 'Disabilita',
          onClick: () => {
            dispatch(setShowBarcodeElement(false))
          },
        },
      ],
    }
  }, [dispatch])

  const notifyBarcode = useCallback(
    (error, showInputMessage, isArticleBarcode) => {
      const notificationMessage =
        error +
        (showInputMessage
          ? isArticleBarcode
            ? ' ' + renderTranslation(language, TranslationKeys.barcodeInputExtraErrorInfo) + ' ' + barCode
            : ': ' + barCode
          : '')
      notification.open({
        description: notificationMessage,
        placement: 'top',
        duration: 3, //default 4.5
        message: '',
      })
      //always reset input when an error is fire
      setBarCode('')
    },
    [language, barCode],
  )

  const searchBarcode = useCallback(
    (brCd, callback) => {
      getBarcode(brCd)
        .then((result: any) => {
          dispatch(saveBarcode({ key: ArticleColumn.TableKey, value: result.variant, barCode: brCd }))
          dispatch(populateTableWithArticles())
          fetchAllArticleDetails(
            result.variant,
            formConfigurationValue,
            movementTabVisible && workspaceMovementVisible,
            dispatch,
          )
          setBarCode('')
          setBarCodeVisible(false)
        })
        .catch(err => {
          handleErrorCatchLog(err, 'Error getting barcode getBarcode')
          if (callback) {
            callback()
            return
          }
          notifyBarcode(renderTranslation(language, TranslationKeys.barcodeGenericError), true, true)
        })
    },
    [language, notifyBarcode, movementTabVisible, workspaceMovementVisible, formConfigurationValue, dispatch],
  )

  const onBarcodeSearch = useCallback(() => {
    setBarCodeVisible(true)
    const barLen = barCode.length

    if (barLen > 2) {
      if (!HandleClientBarcode(barCode, notifyBarcode)) {
        //barcode cliente
        return
      }
    }
    switch (barLen) {
      case 0:
        notifyBarcode(renderTranslation(language, TranslationKeys.barcodeNotProvided), false, false)
        break
      case 4:
        Handle4CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 5:
        Handle5CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 6:
        Handle6CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 7:
        Handle7CharacterBarcode(barCode, searchBarcode)
        break
      case 8:
        Handle8CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 9:
        Handle9CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 10:
        Handle10CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 11:
        Handle11CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 12:
        Handle12CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 13:
        Handle13CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 14:
        Handle14CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 15:
        Handle15CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 16:
        Handle1617CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 17:
        Handle1617CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      case 18:
        Handle18CharacterBarcode(barCode, searchBarcode, notifyBarcode)
        break
      default:
        notifyBarcode(renderTranslation(language, TranslationKeys.barcodeGenericError), true, true)
        break
    }
  }, [barCode, language, notifyBarcode, searchBarcode])

  useEffect(() => {
    if (activeAutocomplete === '' && !barCodeVisible) {
      const handleKeyDown = event => {
        const isLetter = /^[a-z]$/i.test(event.key)
        const isNumber = /^[0-9]$/i.test(event.key)

        if (isLetter || isNumber) {
          const charactersWritten = barCode + event.key
          setBarCode(charactersWritten)
        } else if (event.key === 'Enter') {
          onBarcodeSearch()
        } else {
          setBarCode('')
        }
      }
      window.addEventListener('keydown', handleKeyDown)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [barCode, activeAutocomplete, barCodeVisible, onBarcodeSearch])

  const onBarcodeInputChange = useCallback(e => {
    setBarCode(e.target.value)
  }, [])

  const onBarcodeConfirmByInput = useCallback(
    e => {
      if (!barCodeVisible) {
        return
      }

      onBarcodeSearch()
    },
    [barCodeVisible, onBarcodeSearch],
  )

  return barcodeElementVisibile ? (
    <>
      <Popover content={renderTranslation(language, TranslationKeys.barcodeTips)} trigger='hover'>
        <Dropdown menu={hideSectionMenu} trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}>
          <span className='icon-block'>
            <BarcodeIcon
              className={'generic-icon barcode-icon'}
              onClick={() => {
                setBarCodeVisible(!barCodeVisible)
              }}
            />
          </span>
        </Dropdown>
      </Popover>
      <span className='barcode-input-container'>
        {barCodeVisible ? (
          <Input
            variant={'borderless'}
            size='small'
            value={barCode}
            autoFocus={barCodeVisible}
            onChange={onBarcodeInputChange}
            onPressEnter={onBarcodeConfirmByInput}
            allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
          ></Input>
        ) : (
          <span></span>
        )}
      </span>
    </>
  ) : null
}

export default Barcode
